import { Animated, Easing, Platform, View } from 'react-native';
import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../../../utils/theme';
import {
    BaseTabViewProps,
    SideTabContentWidthVariant,
    StaticContentTabOption,
    StaticSideTabContent,
    isSideTabOption,
    isSideTabOptionGroup,
} from './utils';
import Stack from '../Stack';
import Text from '../Text';
import MainContentContainer, { mainContentPadding } from '../MainContentContainer';
import { ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT } from '../ActionButtonLayout/ActionButtonLayout';
import Divider from '../Divider';
import SideTabButton from './SideTabButton';
import { useForceUpdate } from '../../../hooks/useForceUpdate';
import { ANIMATION_DURATION, SIDE_TAB_WIDTH_VARIANTS } from './constants';
import { IsInSideTabContentContainerContext } from './hooks';
import { PRScrollView } from '../../shared/Scroll';

export type SideTabViewViewOwnProps = Omit<BaseTabViewProps, 'tabs' | 'selectedTab'> & {
    renderHeader?: () => React.ReactNode;
    renderFooter?: () => React.ReactNode;
    tabs: StaticSideTabContent[];
    children: React.ReactNode;
    selectedTab: string | null;
    contentWidth?: SideTabContentWidthVariant;
    renderContentHeader?: () => React.ReactNode;
};
export type SideTabViewViewProps = SideTabViewViewOwnProps;

const SideTabViewView: React.FC<SideTabViewViewProps> = (props) => {
    const { forceUpdate } = useForceUpdate();
    const omitTabs = props.selectedTab === null;
    const [displayAreaWidth, setDisplayAreaWidth] = useState<number | null>(null);

    const selectedTab = props.tabs.filter(isSideTabOption).find((tab) => tab.value === props.selectedTab);
    useEffect(() => {
        forceUpdate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedTab]);
    const curContentWidth: SideTabContentWidthVariant =
        selectedTab?.sideTabContentWidthVariant || props.contentWidth || 'lg';
    const prevContentWidth = useRef<SideTabContentWidthVariant | null>(curContentWidth);
    const contentWidthAnimation = useRef<Animated.Value>(new Animated.Value(SIDE_TAB_WIDTH_VARIANTS[curContentWidth]));
    useEffect(() => {
        if (prevContentWidth.current === 'xl' || curContentWidth === 'xl') {
            contentWidthAnimation.current.setValue(SIDE_TAB_WIDTH_VARIANTS[curContentWidth]);
        } else {
            Animated.timing(contentWidthAnimation.current, {
                toValue: SIDE_TAB_WIDTH_VARIANTS[curContentWidth],
                duration: ANIMATION_DURATION,
                useNativeDriver: Platform.OS === 'web',
                easing: Easing.inOut(Easing.ease),
            }).start();
        }

        if (prevContentWidth.current !== curContentWidth) {
            prevContentWidth.current = curContentWidth;
        }
    }, [curContentWidth]);

    const renderSideTabOption = (tab: StaticContentTabOption) => (
        <SideTabButton
            key={tab.value}
            isDisabled={!!tab.isDisabled}
            isSelected={tab.value === props.selectedTab}
            onClick={() => props.onChange(tab.value)}
            title={tab.title}
            value={tab.value}
            secondaryText={tab.secondaryText}
            iconName={tab.icon}
            inset={tab.inset}
            heavy={tab.heavy}
        />
    );

    const maybeRenderSection = (render: (() => React.ReactNode) | undefined) => {
        if (!render) {
            return null;
        }

        const content = render();
        if (content === null) {
            return null;
        }

        return (
            <View
                style={{
                    padding: 8,
                    alignSelf: 'stretch',
                }}
            >
                {render()}
            </View>
        );
    };

    return (
        <MainContentContainer
            style={{
                width: '100%',
                height: '100%',
                flex: 1,
                padding: omitTabs ? 0 : mainContentPadding,
            }}
        >
            <View
                style={{
                    width: '100%',
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                }}
            >
                {omitTabs ? null : (
                    <Stack
                        vertical
                        style={{
                            marginRight: mainContentPadding,
                            maxHeight: '100%',
                            borderRadius: 8,
                            overflow: 'hidden',
                            backgroundColor: colors.White,
                        }}
                    >
                        {maybeRenderSection(props.renderHeader)}
                        <PRScrollView testID="side-tab-scrollview">
                            <Stack fullWidth vertical padding={2} style={{ padding: 8 }} space={5}>
                                {props.tabs.map((tab) =>
                                    isSideTabOptionGroup(tab) ? (
                                        <Stack key={tab.title} fullWidth vertical padding={2}>
                                            <Stack vertical fullWidth space={2}>
                                                <Text variant="caption-1">{tab.title}</Text>
                                                <Divider horizontal />
                                            </Stack>
                                            <Stack fullWidth vertical style={{ paddingLeft: 8 }}>
                                                {tab.options.map((opt) => (opt.hide ? null : renderSideTabOption(opt)))}
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        renderSideTabOption(tab)
                                    ),
                                )}
                            </Stack>
                        </PRScrollView>
                        {maybeRenderSection(props.renderFooter)}
                    </Stack>
                )}
                <View
                    style={{ flex: 1, height: '100%', overflow: 'hidden' }}
                    onLayout={(e) => setDisplayAreaWidth(e.nativeEvent.layout.width)}
                >
                    <Animated.View
                        style={{
                            width: contentWidthAnimation.current,
                            height: '100%',
                            overflow: 'hidden',
                            maxWidth: '100%',
                        }}
                    >
                        {props.renderContentHeader ? props.renderContentHeader() : null}
                        <Stack
                            vertical
                            flex={1}
                            fullHeight
                            style={{
                                borderRadius: 8,
                                backgroundColor: omitTabs ? undefined : colors.White,
                                overflow: 'hidden',
                            }}
                            align="flex-start"
                            testID="side-tab-content"
                        >
                            <View
                                style={{
                                    width: omitTabs ? '100%' : SIDE_TAB_WIDTH_VARIANTS[curContentWidth],
                                    height: '100%',
                                    overflow: 'hidden',
                                    maxWidth: omitTabs ? undefined : displayAreaWidth || undefined,
                                }}
                            >
                                {selectedTab?.sideTabTitle ? (
                                    <>
                                        <Stack
                                            horizontal
                                            fullWidth
                                            style={{
                                                height: ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT,
                                                backgroundColor: colors.White,
                                            }}
                                            align="center"
                                            justify="flex-start"
                                        >
                                            <MainContentContainer>
                                                <Text variant="h4">{selectedTab.sideTabTitle}</Text>
                                            </MainContentContainer>
                                        </Stack>
                                        <Divider horizontal />
                                    </>
                                ) : null}
                                <IsInSideTabContentContainerContext.Provider value={!omitTabs}>
                                    {props.children}
                                </IsInSideTabContentContainerContext.Provider>
                            </View>
                        </Stack>
                    </Animated.View>
                </View>
            </View>
        </MainContentContainer>
    );
};

export default React.memo(SideTabViewView);
